import type { Question } from 'schema/poll';
import { encrypt, isE2EEnabled } from '../crypto';

const PROTECTED_DATA_TEXTAREA_TYPES = ['PHONE', 'EMAIL'];
const SAFE_TEXTAREA_TYPES = ['PHONE', 'EMAIL', 'DATE'];

/** Проверяет необходимо ли шифровать ввод пользователя */
export function questionReplaceUnsafe(question: Question) {
  if ('hasOthers' in question.data && question.data.hasOthers) return !!question.data.othersReplaceUnsafe;
  if ('hasComment' in question.data && question.data.hasComment) return !!question.data.commentReplaceUnsafe;
  if (question.data.type === 'POLL_TEXTAREA') {
    if (isE2EEnabled && PROTECTED_DATA_TEXTAREA_TYPES.includes(question.data.textareaType)) return true;
    if (!isE2EEnabled && SAFE_TEXTAREA_TYPES.includes(question.data.textareaType)) return false;
    return !!question.data.textReplaceUnsafe;
  }
  return false;
}

/** Шифрует ввод пользователя */
export async function replaceUnsafeInput(text: string): Promise<string> {
  if (typeof text === 'string') {
    /** Если включено E2E шифрование то шифруем текст в любом случае */
    if (isE2EEnabled) return encrypt(text, { cause: 'e2e' });
    /** Если в тексте присутствуют персональные данные шифруем ввод */
    if (needReplaceUnsafeMessage(text)) return encrypt(text, { cause: 'pp' });
  }
  return text;
}

/** Регулярки взял из @dlp/blackout-sync */
const EMAIL_RFC_REGEXP =
  // eslint-disable-next-line no-control-regex
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
const PHONE_COMMON_REGEXP = /((?:\+?(\d{1,3}))?[-.(]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?)/;
const PASSPORT_COMMON_REGEXES = [
  /\d{4}\s\d{6}/,
  /\d{2}\s\d{7}/,
  /[A-Za-z]{1}\d{8}/,
  /\d{2}\s\d{2}\s\d{6}/,
  /[А-Яа-я]{2}\d{7}/,
  /[А-Яа-я]{2}\d{6}/,
  /[A-Za-z]{2}\d{7}/,
  /[A-Za-z]{2}\d{7}/,
];
const BANKS_NUMBER_COMMON_REGEXES = [
  /(\d( *)){12,18}\d/,
  /(\b(?:\d{10}|\d{12})\b)/, //ИНН
  /(\b\d{13}\b)/, //ОГРН
  /(\b\d{15}\b)/, //ОГРНИП
  /\b[0-9]{4}[0-9A-Z]{2}[0-9]{3}\b/, //КПП
  /(\b\d{9}\b)/, //БИК
  /(\b301\d{17}\b)/, //Корреспондентский счёт
  /(\b40[5678]\d{17}\b)/, //Платёжный счёт
];
const REGION_PHONE_COMMON_REGEXP = /(\d{2})-(\d{2})-(\d{2})/;
const DIGITS_COMMON_REGEXP = /\d{3}/;

export function needReplaceUnsafeMessage(text: string): boolean {
  return Boolean(
    EMAIL_RFC_REGEXP.test(text) ||
      PHONE_COMMON_REGEXP.test(text) ||
      DIGITS_COMMON_REGEXP.test(text) ||
      REGION_PHONE_COMMON_REGEXP.test(text) ||
      PASSPORT_COMMON_REGEXES.find(regexp => regexp.test(text)) ||
      BANKS_NUMBER_COMMON_REGEXES.find(regexp => regexp.test(text)),
  );
}
